import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Languages, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import RichTextEditor from './RichTextEditor';
import { AiTextHelper } from './AiTextHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { code: 'bg', label: 'БГ', name: 'български' },
  { code: 'el', label: 'GR', name: 'гръцки' },
  { code: 'tr', label: 'TR', name: 'турски' },
  { code: 'en', label: 'EN', name: 'английски' },
];

/**
 * Tabbed description editor for BG (primary) + EL/TR/EN, with AI translate
 * buttons that turn the Bulgarian text into the other three languages.
 * Empty language tabs fall back to the Bulgarian text on the public site.
 *
 * values: { bg, el, tr, en }
 * onChange: (langCode, text) => void
 */
export default function MultilingualDescriptionEditor({ values, onChange, context = '', richText = false, placeholder = '', required = false }) {
  const [activeTab, setActiveTab] = useState('bg');
  const [translating, setTranslating] = useState(null);

  const hasContent = (lang) => !!(values[lang] && values[lang].trim());

  const translateTo = async (targetLang) => {
    const source = values.bg;
    if (!source || !source.trim()) {
      toast.error('Първо въведи описание на български');
      return null;
    }
    setTranslating(targetLang);
    try {
      const response = await axios.post(`${API}/ai/generate`, {
        prompt: source.replace(/<[^>]+>/g, ' '), // strip HTML tags before sending to AI
        context,
        action: 'translate',
        target_language: targetLang,
      });
      onChange(targetLang, response.data.result);
      return response.data.result;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Грешка при превода');
      return null;
    } finally {
      setTranslating(null);
    }
  };

  const translateAll = async () => {
    if (!values.bg || !values.bg.trim()) {
      toast.error('Първо въведи описание на български');
      return;
    }
    for (const lang of ['el', 'tr', 'en']) {
      // eslint-disable-next-line no-await-in-loop
      await translateTo(lang);
    }
    toast.success('Преведено на гръцки, турски и английски');
  };

  const activeLang = LANGS.find(l => l.code === activeTab);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 bg-secondary rounded-lg p-1">
          {LANGS.map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => setActiveTab(l.code)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                activeTab === l.code ? 'bg-white shadow-sm text-[#1B5E37]' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`desc-lang-tab-${l.code}`}
            >
              {l.label}
              {l.code !== 'bg' && hasContent(l.code) && <Check className="w-3 h-3 text-[#1B5E37]" />}
            </button>
          ))}
        </div>

        {activeTab === 'bg' ? (
          <Button type="button" variant="outline" size="sm" onClick={translateAll} disabled={!!translating} className="h-7 text-xs gap-1.5">
            {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
            Преведи на всички езици
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => translateTo(activeTab)} disabled={translating === activeTab} className="h-7 text-xs gap-1.5">
            {translating === activeTab ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
            {hasContent(activeTab) ? `Преведи наново на ${activeLang.name}` : `Преведи от български`}
          </Button>
        )}
      </div>

      {richText ? (
        <RichTextEditor
          value={values[activeTab] || ''}
          onChange={(html) => onChange(activeTab, html)}
          rows={5}
        />
      ) : (
        <Textarea
          value={values[activeTab] || ''}
          onChange={(e) => onChange(activeTab, e.target.value)}
          rows={3}
          required={required && activeTab === 'bg'}
          placeholder={activeTab === 'bg' ? placeholder : `Описание на ${activeLang.name}...`}
        />
      )}

      {activeTab === 'bg' && (
        <AiTextHelper
          value={values.bg}
          onResult={(text) => onChange('bg', text)}
          context={context}
          placeholder={placeholder}
        />
      )}

      {activeTab !== 'bg' && !hasContent(activeTab) && (
        <p className="text-xs text-muted-foreground">
          Няма въведен текст за {activeLang.name} — посетителите от този пазар ще виждат българското описание вместо това.
        </p>
      )}
    </div>
  );
}
